import React from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import videojs from 'video.js';
import type {ReadyCallback} from 'video.js/dist/types/component';
import type Player from 'video.js/dist/types/player';

import {ReactComponent as DownloadIcon} from '../../assets/SVG/icon-download.svg';
import {ReactComponent as LoadingIcon} from '../../assets/SVG/icon-loading.svg';

type DownloadURLRef = React.RefObject<string | null>;
type VideoJsComponentOptions = {
  children?: any[];
  className?: string;
};

export type DownloadButtonOptions = VideoJsComponentOptions & {
  downloadUrlRef?: DownloadURLRef;
};

type SvgIconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

function renderIconMarkup(Icon: SvgIconComponent): string {
  return renderToStaticMarkup(<Icon />);
}

function setContainerIcon(container: HTMLElement, Icon: SvgIconComponent) {
  container.innerHTML = renderIconMarkup(Icon);
  const svg = container.querySelector('svg');
  if (svg) {
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
  }
}

const Button = videojs.getComponent('Button');
const ICON_CONTAINER_CLASS = 'vjs-download-button__icon-container';

export class DownloadButton extends Button {
  downloadUrlRef: DownloadURLRef | null = null;
  isLoading: boolean = false;
  waitIntervalId_: number | null = null;

  constructor(
    player: Player,
    options?: DownloadButtonOptions,
    ready?: ReadyCallback
  ) {
    super(player, options, ready);
    this.downloadUrlRef = options?.downloadUrlRef ?? null;
    this.setupIcon();
  }

  buildCSSClass() {
    return `vjs-download-button ${super.buildCSSClass()}`;
  }

  createEl() {
    const el = super.createEl() as HTMLElement;
    el.style.width = 'auto';
    el.setAttribute('aria-label', 'Download video');
    el.setAttribute('title', 'Download video');

    return el;
  }

  setupIcon(): HTMLElement | null {
    const el = this.el() as HTMLElement | undefined;
    if (!el || el.querySelector(`.${ICON_CONTAINER_CLASS}`)) {
      return null;
    }

    const iconContainer = document.createElement('span');
    iconContainer.className = ICON_CONTAINER_CLASS;
    iconContainer.style.color = 'white';
    iconContainer.style.padding = '2px';
    iconContainer.style.display = 'inline-flex';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.justifyContent = 'center';
    iconContainer.style.position = 'relative';
    iconContainer.style.zIndex = '1';
    setContainerIcon(iconContainer, DownloadIcon);
    el.appendChild(iconContainer);
    return iconContainer;
  }

  getIconContainer(): HTMLElement | null {
    return this.el()?.querySelector(`.${ICON_CONTAINER_CLASS}`) ?? null;
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;

    const iconContainer = this.getIconContainer() || this.setupIcon();
    if (!iconContainer) {
      return;
    }

    setContainerIcon(iconContainer, loading ? LoadingIcon : DownloadIcon);
  }

  getDownloadUrl(): string | null {
    return this.downloadUrlRef?.current ?? null;
  }

  stopWaitingForDownloadUrl() {
    if (this.waitIntervalId_ != null) {
      this.clearInterval(this.waitIntervalId_);
      this.waitIntervalId_ = null;
    }
  }

  waitForDownloadUrl() {
    if (this.waitIntervalId_ != null) {
      return;
    }

    this.setLoading(true);

    this.waitIntervalId_ = this.setInterval(() => {
      const downloadUrl = this.getDownloadUrl();
      if (downloadUrl) {
        this.stopWaitingForDownloadUrl();
        this.setLoading(false);
        this.downloadFile(downloadUrl);
      }
    }, 100);
  }

  handleClick() {
    if (this.isLoading) {
      return;
    }

    const downloadUrl = this.getDownloadUrl();
    if (!downloadUrl) {
      this.waitForDownloadUrl();
      return;
    }

    this.downloadFile(downloadUrl);
  }

  downloadFile(downloadUrl: string) {
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export function downloadPlugin(this: Player, options?: DownloadButtonOptions) {
  this.ready(() => {
    // Add download button as second to last (before fullscreen button)
    const controlBar = this.getChild('controlBar');
    const childrenLength = controlBar?.children().length || 0;
    controlBar?.addChild('downloadButton', options || {}, childrenLength - 1);
  });
}
